import React, { useContext, useMemo, useState } from 'react';
import {
    EntityValidations,
    ISpecificEntityFormProps,
    IApp03Sprint,
    SaveStatus,
    App03Sprint,
    CustomField
} from '../../types';
import { DataContext } from '../DataContext';
import { CrTextField } from '../cr/CrTextField';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrDropdown } from '../cr/CrDropdown';
import { CrCommandBar } from '../cr/CrCommandBar';
import { EntityForm } from '../EntityForm';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import useMobileDetector from '../useMobileDetector';
import { App03SprintStatus } from '../../types/App03Sprint';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';
import SprintEvidenceList from './EV/SprintEvidenceList';
import { OrbUserContext } from '../OrbUserContext';
import { App03Frameworks } from '../../types/App03Workflow';

class SprintFormValidations extends EntityValidations {
    public StartDate: string = null;
    public EndDate: string = null;
    public SprintStatus: string = null;
}

interface ISprintFormProps extends ISpecificEntityFormProps {
    frameworkID: App03Frameworks;
}

export const SprintFormPage = (props: ISprintFormProps): React.ReactElement => {
    const { dataServices, lookupData, loadLookupData: { customFieldDfns } } = useContext(DataContext);
    const { userContext, userPermissions } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const isMobile = useMobileDetector();
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');

    useMemo(() => customFieldDfns(), [customFieldDfns]);

    const validateEntity = (sprint: IApp03Sprint): Promise<SprintFormValidations> => {
        const errors = new SprintFormValidations();

        if (!sprint.Title || sprint.Title.trim() === '') {
            errors.Title = 'Title is required';
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (sprint.StartDate == null) {
            errors.StartDate = 'Start Date is required';
            errors.Valid = false;
        }
        else {
            errors.StartDate = null;
        }

        if (sprint.EndDate == null) {
            errors.EndDate = 'End Date is required';
            errors.Valid = false;
        }
        else {
            errors.EndDate = null;
        }

        if (sprint.StartDate && sprint.EndDate && sprint.EndDate < sprint.StartDate) {
            errors.EndDate = 'End date cannot be earlier than start date';
            errors.Valid = false;
        }


        if (sprint.SprintStatus == null) {
            errors.SprintStatus = 'Sprint Status is required';
            errors.Valid = false;
        }
        else {
            errors.SprintStatus = null;
        }

        return Promise.resolve(errors);
    };

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'Agile Sprints - Sprints',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }

    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
            <EntityForm<IApp03Sprint, SprintFormValidations>
                {...props}
                entityName="Sprint"
                mode="page"
                renderFormFields={(changeHandlers, formState, saveEntity) => {
                    const { FormData: formData, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;

                    const commandBarItems = FormSaveStatus === SaveStatus.Pending
                        ? [{
                            key: 'Save',
                            text: 'Saving...',
                            iconProps: { iconName: 'Sync' },
                            disabled: true,
                        }]
                        : !FormIsDirty
                            ? [{
                                key: 'Close',
                                text: 'Close',
                                iconProps: { iconName: 'Cancel' },
                                onClick: () => props.onCancelled(),
                            }]
                            : [
                                {
                                    key: 'Save',
                                    text: 'Save',
                                    iconProps: { iconName: 'Save' },
                                    onClick: () => saveEntity(),
                                },
                                {
                                    key: 'Cancel',
                                    text: 'Cancel',
                                    iconProps: { iconName: 'Cancel' },
                                    onClick: () => props.onCancelled(),
                                }
                            ];

                    const startLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Start Date</span></div>
                                <div className={styles.sectionQuestionCol2}></div>
                            </div>
                        </div>
                    );

                    const endLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>End Date</span></div>
                                <div className={styles.sectionQuestionCol2}></div>
                            </div>
                        </div>
                    );

                    const velocityTargetLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Velocity Target</span></div>
                                <div className={styles.sectionQuestionCol2}></div>
                            </div>
                        </div>
                    );

                    const actualVelocityLabel = (
                        <div className='two-fields-sidebyside-lbl'>
                            <div className={styles.flexContainerSectionQuestion}>
                                <div className={styles.sectionQuestionCol1}><span>Actual Velocity</span></div>
                                <div className={styles.sectionQuestionCol2}></div>
                            </div>
                        </div>
                    );


                    return (
                        <div>
                            <CrCommandBar items={commandBarItems} />

                            {/* Sprint Details Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '10px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Sprint Details</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* Title */}
                                    <div className={styles.formField}>
                                        <div className={styles.flexContainerSectionQuestion}>
                                            <div className={styles.sectionQuestionCol1}><span>Title</span></div>
                                        </div>
                                        <CrTextField
                                            maxLength={200}
                                            value={formData.Title}
                                            onChange={(ev, val) => changeHandlers.changeTextField(val, 'Title')}
                                            errorMessage={errors.Title}
                                            required
                                        />
                                    </div>

                                    {/* Details */}
                                    <div className={styles.formField}>
                                        <div className={styles.flexContainerSectionQuestion}>
                                            <div className={styles.sectionQuestionCol1}><span>Details</span></div>
                                        </div>
                                        <CrTextField
                                            multiline
                                            rows={5}
                                            maxLength={2000}
                                            value={formData.Description}
                                            onChange={(ev, val) => changeHandlers.changeTextField(val, 'Description')}
                                        />
                                    </div>

                                    {/* Start and End Dates */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && (
                                            <div className='two-fields-sidebyside-r1'>
                                                {startLabel}
                                                {endLabel}
                                            </div>
                                        )}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && startLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.StartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'StartDate')}
                                                    required={true}
                                                    errorMessage={errors.StartDate}
                                                />
                                            </div>
                                            {isMobile && endLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrDatePicker
                                                    maxWidth='100%'
                                                    value={DateService.removeTimezoneOffset(formData.EndDate)}
                                                    minDate={DateService.removeTimezoneOffset(formData.StartDate)}
                                                    onSelectDate={date => changeHandlers.changeDatePicker(date, 'EndDate')}
                                                    required={true}
                                                    errorMessage={errors.EndDate}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className={styles.formField}>
                                        <div className={styles.flexContainerSectionQuestion}>
                                            <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                                        </div>
                                        <CrDropdown
                                            options={[
                                                { key: App03SprintStatus.Planned, text: "Planned" },
                                                { key: App03SprintStatus.Active, text: "Active" },
                                                { key: App03SprintStatus.Completed, text: "Completed" },
                                            ]}
                                            selectedKey={formData.SprintStatus}
                                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'SprintStatus')}
                                            required={true}
                                            errorMessage={errors.SprintStatus}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Velocity Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Velocity</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        {isMobile !== true && (
                                            <div className='two-fields-sidebyside-r1'>
                                                {velocityTargetLabel}
                                                {actualVelocityLabel}
                                            </div>
                                        )}
                                        <div className='two-fields-sidebyside-r2'>
                                            {isMobile && velocityTargetLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    maxLength={3}
                                                    value={formData.VelocityTarget}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField_number(newValue, 'VelocityTarget')}
                                                //errorMessage={errors.Title}
                                                //required={true}
                                                />
                                            </div>
                                            {isMobile && actualVelocityLabel}
                                            <div className='two-fields-sidebyside-field'>
                                                <CrTextField
                                                    maxLength={3}
                                                    value={formData.ActualVelocity}
                                                    onChange={(ev, newValue) => changeHandlers.changeTextField_number(newValue, 'ActualVelocity')}
                                                //errorMessage={errors.Title}
                                                //required={true}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Additional Information Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Additional Information</div>
                                    </div>
                                </div>

                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Additional Information</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrCustomFields
                                                    customFieldDfns={filteredCustomFieldDfns}
                                                    selectedItems={LookupService.customFieldsToDropdownWithText(formData.CustomFields)}
                                                    onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </div>
                    );
                }}
                loadEntity={id => dataServices.app03SprintService.read(id, true, true)}
                loadNewEntity={() => new App03Sprint()}
                loadEntityValidations={() => new SprintFormValidations()}
                onValidateEntity={validateEntity}
                onCreate={entity => dataServices.app03SprintService.create(entity)}
                onUpdate={entity => dataServices.app03SprintService.update(entity.ID, entity)}
                parentEntities={dataServices.app03SprintService.parentEntities}
                childEntities={[
                    {
                        ObjectParentProperty: 'CustomFields',
                        ParentIdProperty: 'App03SprintID',
                        ChildIdProperty: 'CustomFieldDefnID',
                        ChildService: dataServices.customFieldService
                    }
                ]}
            />

            {props.entityId > 0 &&
                <>
                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Sprint Discussion, General Comments and File Attachments</div>
                            </div>
                        </div>
                    </div>
                    <div>

                        <div style={{ minHeight: '120px' }}>
                            <SprintEvidenceList
                                frameworkID={props.frameworkID}
                                sprintID={props.entityId}
                                currentUserId={currentUserId}
                                superUserPermission={userPermissions.UserIsSystemAdmin()}
                                isViewOnly={false}
                                filterText={evidence_ListFilterText}
                                onChangeFilterText={handleEvidence_ChangeFilterText}
                                evChangesCounter={1}
                                {...props}
                                errorHandling={props.errorHandling}
                            />
                        </div>

                    </div>
                </>
            }

        </div>
    );
};
